import * as Sentry from '@sentry/react';
import { generators } from '@playcastdotio/Messaging';
import { ipcSend } from '../../../../api/messaging/protocols/ipc';
import type { PlaycastTargetButton } from '@playcastdotio/CoreTypes';
import type { ParsedMouseStep } from '../MacroStepParser';

export type MacroGuestFrame = { width: number; height: number };

export interface MacroMouseClick {
  abs: ParsedMouseStep;
  down: ParsedMouseStep;
}

export interface MacroMouseEmitter {
  /** Resolves false when the click could not be placed. No button is pressed. */
  emitClick(click: MacroMouseClick, frame: MacroGuestFrame): Promise<boolean>;
}

const MOUSE_BUTTON_LOOKUP: PlaycastTargetButton[] = ['left', 'middle', 'right', 'back', 'forward'];

function numericButtonToTarget(button: number): PlaycastTargetButton {
  return MOUSE_BUTTON_LOOKUP[button] ?? 'left';
}

const ZERO_VEC = { x: 0, y: 0 };
const DEFAULT_PLAYER_COORDS = {
  origin: 'bottomLeft' as const,
  dimensions: ZERO_VEC,
  display: ZERO_VEC,
};

export const MOUSE_MODE_ABSOLUTE = {
  mode: 'absolute' as const,
  deviceType: 'mouse' as const,
  isBounded: true,
  wrapMouse: false,
  deviceId: -1,
};

export const MOUSE_MODE_DEFAULT = {
  ...MOUSE_MODE_ABSOLUTE,
  mode: 'default' as const,
};

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isValidGuestFrame(frame?: MacroGuestFrame): frame is MacroGuestFrame {
  return Boolean(frame && frame.width > 0 && frame.height > 0);
}

function guestPixels(x: number, y: number, frame: MacroGuestFrame): { x: number; y: number } {
  return { x: Math.round(x * frame.width), y: Math.round(y * frame.height) };
}

export class RealMacroMouseEmitter implements MacroMouseEmitter {
  public constructor(
    private readonly onFirstAbsoluteMode: () => void,
    private readonly label: string,
  ) {}

  public async emitClick(click: MacroMouseClick, frame: MacroGuestFrame): Promise<boolean> {
    if (!isValidGuestFrame(frame)) {
      return false;
    }

    this.onFirstAbsoluteMode();

    const x = click.abs.params.x ?? 0;
    const y = click.abs.params.y ?? 0;
    const position = guestPixels(x, y, frame);
    const positionFrame = { x: frame.width, y: frame.height };
    console.log('[MacroManager] Sending mouse=abs', {
      label: this.label,
      normalized: { x, y },
      position,
      positionFrame,
      frame,
    });
    Sentry.logger.info(
      `[MacroManager] Sending mouse=abs norm=${x},${y} px=${position.x},${position.y} `
        + `frame=${frame.width}x${frame.height} positionFrame=${positionFrame.x}x${positionFrame.y}`,
    );

    // Atomic absolute click: carry position on down so Playjector applies
    // location then button in one handler (no host-side sleep / ACK race).
    ipcSend(generators.mouse.down('host', {
      playerCoordinates: DEFAULT_PLAYER_COORDS,
      position,
      delta: ZERO_VEC,
      positionFrame,
      button: numericButtonToTarget(click.down.params.button ?? 0),
      clickCount: 1,
    }));
    await delay(20);
    return true;
  }

  public async sendMouseAction(step: ParsedMouseStep, frame?: MacroGuestFrame): Promise<void> {
    switch (step.action) {
      case 'move':
        ipcSend(generators.mouse.move('host', {
          playerCoordinates: DEFAULT_PLAYER_COORDS,
          position: ZERO_VEC,
          delta: { x: step.params.dx ?? 0, y: step.params.dy ?? 0 },
        }));
        break;
      case 'down':
        ipcSend(generators.mouse.down('host', {
          playerCoordinates: DEFAULT_PLAYER_COORDS,
          position: ZERO_VEC,
          delta: ZERO_VEC,
          button: numericButtonToTarget(step.params.button ?? 0),
          clickCount: 1,
        }));
        break;
      case 'up':
        ipcSend(generators.mouse.up('host', {
          playerCoordinates: DEFAULT_PLAYER_COORDS,
          position: ZERO_VEC,
          delta: ZERO_VEC,
          button: numericButtonToTarget(step.params.button ?? 0),
          clickCount: 0,
        }));
        break;
      case 'wheel':
        ipcSend(generators.mouse.wheel('host', {
          playerCoordinates: DEFAULT_PLAYER_COORDS,
          position: ZERO_VEC,
          scroll: { x: step.params.deltaX ?? 0, y: step.params.deltaY ?? 120 },
          mode: 0,
          units: 'wheel-delta',
        }));
        break;
      case 'abs': {
        if (!isValidGuestFrame(frame)) {
          console.warn('[MacroManager] Skipping mouse=abs — guest frame missing or invalid', {
            label: this.label,
            frame: frame ?? null,
            normalized: { x: step.params.x, y: step.params.y },
          });
          break;
        }
        const x = step.params.x ?? 0;
        const y = step.params.y ?? 0;
        const position = guestPixels(x, y, frame);
        const positionFrame = { x: frame.width, y: frame.height };
        this.onFirstAbsoluteMode();
        console.log('[MacroManager] Sending mouse=abs', {
          label: this.label,
          normalized: { x, y },
          position,
          positionFrame,
          frame,
        });
        Sentry.logger.info(
          `[MacroManager] Sending mouse=abs norm=${x},${y} px=${position.x},${position.y} `
            + `frame=${frame.width}x${frame.height} positionFrame=${positionFrame.x}x${positionFrame.y}`,
        );
        ipcSend(generators.mouse.setLocation('host', {
          playerCoordinates: DEFAULT_PLAYER_COORDS,
          position,
          delta: ZERO_VEC,
          positionFrame,
        }));
        break;
      }
    }
    if (step.action !== 'wheel') await delay(20);
  }
}

export function isValidMacroGuestFrame(frame?: MacroGuestFrame): frame is MacroGuestFrame {
  return isValidGuestFrame(frame);
}

export function isRecordedClickPair(
  current: ParsedMouseStep,
  next: ParsedMouseStep | null | undefined,
): next is ParsedMouseStep {
  return current.action === 'abs' && next?.action === 'down';
}

/** Thrown when a recorded click cannot be placed — aborts the remaining macro steps. */
export class UnplaceableClickError extends Error {
  public constructor(label: string) {
    super(`[MacroStepRunner] Recorded click could not be placed for ${label}`);
    this.name = 'UnplaceableClickError';
  }
}
